import axios, { AxiosInstance, AxiosResponse } from "axios";
import {
  User,
  Company,
  Country,
  Workflow,
  BackendWorkflow,
  Activity,
  WorkflowExecution,
  ApiResponse,
  LoginRequest,
  LoginResponse,
  WorkflowContainer,
  WorkflowFormValueSuggestion,
  ActivityDefinition,
  Form,
  ActivityReport,
  ActivityReportListResponse,
  ActivityReportFilters,
  GoogleDriveAttachment,
} from "../types";
import { API_BASE_URL } from "../config/api";
import {
  clearStoredAuthUser,
  notifyAuthStateInvalidated,
} from "../utils/authSession";

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true, // Include cookies in requests
    });

    // Remove the token interceptor since we're using HTTP-only cookies
    // Token is automatically sent via cookies

    // Handle token expiration
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          clearStoredAuthUser();
          notifyAuthStateInvalidated("unauthorized");
        }
        return Promise.reject(error);
      }
    );
  }

  private toFrontendWorkflow(backendWorkflow: any): Workflow {
    return {
      ...backendWorkflow,
      nodes:
        backendWorkflow.nodes?.map((node: any) => {
          const formData =
            node.data?.taskFormData || node.formData || node.data?.formData;
          const label =
            node.data?.label ||
            formData?.activityName ||
            formData?.formName ||
            node.label ||
            "";

          return {
            id: node.id,
            type: String(node.type || "").toUpperCase(),
            position: {
              x: node.position?.x ?? node.positionX ?? 0,
              y: node.position?.y ?? node.positionY ?? 0,
            },
            data: {
              ...(node.data || {}),
              label,
              description: node.data?.description ?? node.description,
              assigneeId: node.data?.assigneeId ?? node.assigneeId,
              assigneeName: node.data?.assigneeName ?? node.assigneeName,
              activityDefinitionId:
                node.data?.activityDefinitionId ?? node.activityId,
              formData,
              taskFormData: node.data?.taskFormData ?? formData,
              conditions: node.data?.conditions ?? node.conditions,
              notificationMessage:
                node.data?.notificationMessage ?? node.notificationMessage,
              approvalRequired:
                node.data?.approvalRequired ?? node.approvalRequired,
              dueDate: node.data?.dueDate ?? node.dueDate,
            },
            workflowId: backendWorkflow.id,
          };
        }) || [],
      edges:
        backendWorkflow.edges?.map((edge: any) => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
          sourceHandle: edge.sourceHandle,
          targetHandle: edge.targetHandle,
          condition: edge.condition,
          workflowId: backendWorkflow.id,
        })) || [],
    };
  }

  // Auth endpoints
  async register(userData: {
    firstName: string;
    lastName: string;
    username: string;
    email: string;
    password: string;
    role: string;
    companyId?: number;
    allowedWorkflowIds?: number[];
    allowedWorkflowInstanceIds?: number[];
    visitorFinancialAccess?: boolean;
  }): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(
      "/auth/register",
      userData
    );
    return response.data;
  }

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response: AxiosResponse<any> = await this.api.post(
      "/auth/login",
      credentials
    );
    // Return user data, token is automatically set as HTTP-only cookie
    return {
      token: "", // Not needed anymore, cookie handles this
      user: response.data.user,
    };
  }

  async getCurrentUser(): Promise<User> {
    const response: AxiosResponse<User> = await this.api.get("/auth/me");
    return response.data;
  }

  async logout(): Promise<void> {
    await this.api.post("/auth/logout");
    clearStoredAuthUser();
  }

  // Company endpoints
  async getCompanies(): Promise<Company[]> {
    const response: AxiosResponse<Company[]> = await this.api.get("/companies");
    return response.data;
  }

  // Country endpoints
  async getCountries(): Promise<Country[]> {
    const response: AxiosResponse<Country[]> = await this.api.get("/countries");
    return response.data;
  }

  async getLocationSuggestions(params: {
    country: string;
    province?: string;
    district?: string;
  }): Promise<{
    provinces: string[];
    districts: string[];
    neighborhoods: string[];
  }> {
    const response: AxiosResponse<{
      provinces: string[];
      districts: string[];
      neighborhoods: string[];
    }> = await this.api.get("/location-suggestions", { params });
    return response.data;
  }

  // User endpoints
  async getUsers(
    includeSelf: boolean = false,
    includeAdmins: boolean = false
  ): Promise<User[]> {
    const response: AxiosResponse<ApiResponse<User[]>> = await this.api.get(
      "/users",
      {
        params: {
          includeSelf,
          includeAdmins,
        },
      }
    );
    return response.data.data;
  }

  async getUserById(id: number): Promise<User> {
    const response: AxiosResponse<ApiResponse<User>> = await this.api.get(
      `/users/${id}`
    );
    return response.data.data;
  }

  async createUser(
    user: Partial<User> & {
      password?: string;
      allowedWorkflowIds?: number[];
      allowedWorkflowInstanceIds?: number[];
      visitorFinancialAccess?: boolean;
    }
  ): Promise<User> {
    const response: AxiosResponse<ApiResponse<User>> = await this.api.post(
      "/users",
      user
    );
    return response.data.data;
  }

  async updateUser(
    id: number,
    user: Partial<User> & {
      password?: string;
      allowedWorkflowIds?: number[];
      allowedWorkflowInstanceIds?: number[];
      visitorFinancialAccess?: boolean;
    }
  ): Promise<User> {
    const response: AxiosResponse<ApiResponse<User>> = await this.api.put(
      `/users/${id}`,
      user
    );
    return response.data.data;
  }

  async deleteUser(id: number): Promise<void> {
    await this.api.delete(`/users/${id}`);
  }

  async searchUsers(username: string): Promise<User[]> {
    const response: AxiosResponse<ApiResponse<User[]>> = await this.api.get(
      `/users/search?username=${encodeURIComponent(username)}`
    );
    return response.data.data;
  }

  async getUserSettings(): Promise<Record<string, any>> {
    const response: AxiosResponse<ApiResponse<Record<string, any>>> =
      await this.api.get("/users/me/settings");
    return response.data.data || {};
  }

  async updateUserSettings(
    settings: Record<string, any>
  ): Promise<Record<string, any>> {
    const response: AxiosResponse<ApiResponse<Record<string, any>>> =
      await this.api.put("/users/me/settings", settings);
    return response.data.data || {};
  }

  // Workflow endpoints
  async getWorkflows(): Promise<Workflow[]> {
    const response: AxiosResponse<ApiResponse<Workflow[]>> = await this.api.get(
      "/workflows"
    );
    return response.data.data;
  }

  async getProjectWorkflow(projectId: number): Promise<Workflow | null> {
    const response: AxiosResponse<ApiResponse<Workflow[]>> = await this.api.get(
      `/workflows?projectId=${projectId}`
    );
    // Her projede sadece 1 workflow olduğu için ilkini döndür
    return response.data.data.length > 0 ? response.data.data[0] : null;
  }

  async getWorkflowById(id: number): Promise<Workflow> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(
      `/workflows/${id}`
    );

    if (response.data.data) {
      return this.toFrontendWorkflow(response.data.data);
    }

    const backendWorkflow = response.data.data;

    // Convert backend format to frontend format
    const frontendWorkflow: Workflow = {
      ...backendWorkflow,
      nodes:
        backendWorkflow.nodes?.map((node: any) => ({
          id: node.id,
          type: node.type.toUpperCase(), // Convert to uppercase to match NodeType enum
          position: {
            x: node.positionX || 0,
            y: node.positionY || 0,
          },
          data: {
            label: node.label,
            description: node.description,
            assigneeId: node.assigneeId,
            assigneeName: node.assigneeName,
            activityDefinitionId: node.activityId, // Map activityId to activityDefinitionId for frontend
            // ✅ If node has activity, TaskFormPanel will load its details via pool activity logic
            conditions: node.conditions,
            notificationMessage: node.notificationMessage,
            approvalRequired: node.approvalRequired,
            dueDate: node.dueDate,
          },
          workflowId: backendWorkflow.id,
        })) || [],
      edges:
        backendWorkflow.edges?.map((edge: any) => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
          sourceHandle: edge.sourceHandle,
          targetHandle: edge.targetHandle,
          condition: edge.condition,
          workflowId: backendWorkflow.id,
        })) || [],
    };

    return frontendWorkflow;
  }

  async getWorkflowProjectManagers(workflowId: number): Promise<number[]> {
    const response: AxiosResponse<ApiResponse<number[]>> = await this.api.get(
      `/workflows/${workflowId}/project-managers`
    );
    return response.data.data || [];
  }

  async setWorkflowProjectManagers(
    workflowId: number,
    projectManagerIds: number[]
  ): Promise<void> {
    await this.api.put(`/workflows/${workflowId}/project-managers`, {
      projectManagerIds,
    });
  }

  async createWorkflow(workflow: Partial<BackendWorkflow>): Promise<Workflow> {
    const response: AxiosResponse<ApiResponse<Workflow>> = await this.api.post(
      "/workflows",
      workflow
    );
    return this.toFrontendWorkflow(response.data.data);
  }

  async updateWorkflow(
    id: number,
    workflow: Partial<BackendWorkflow>
  ): Promise<Workflow> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(
      `/workflows/${id}`,
      workflow
    );

    if (response.data.data) {
      return this.toFrontendWorkflow(response.data.data);
    }

    const backendWorkflow = response.data.data;

    // Convert backend format to frontend format (same as getWorkflowById)
    const frontendWorkflow: Workflow = {
      ...backendWorkflow,
      nodes:
        backendWorkflow.nodes?.map((node: any) => ({
          id: node.id,
          type: node.type.toUpperCase(), // Convert to uppercase to match NodeType enum
          position: {
            x: node.positionX || 0,
            y: node.positionY || 0,
          },
          data: {
            label: node.label,
            description: node.description,
            assigneeId: node.assigneeId,
            assigneeName: node.assigneeName,
            activityDefinitionId: node.activityId, // Map activityId to activityDefinitionId for frontend
            // ✅ Removed formData mapping - forms are now stored in database and linked via activities
            conditions: node.conditions,
            notificationMessage: node.notificationMessage,
            approvalRequired: node.approvalRequired,
            dueDate: node.dueDate,
          },
          workflowId: backendWorkflow.id,
        })) || [],
      edges:
        backendWorkflow.edges?.map((edge: any) => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
          sourceHandle: edge.sourceHandle,
          targetHandle: edge.targetHandle,
          condition: edge.condition,
          workflowId: backendWorkflow.id,
        })) || [],
    };
    return frontendWorkflow;
  }

  async deleteWorkflow(id: number): Promise<void> {
    await this.api.delete(`/workflows/${id}`);
  }

  async deleteWorkflowInstance(id: number): Promise<void> {
    await this.api.delete(`/workflows/instances/${id}`);
  }

  async activateWorkflow(id: number): Promise<void> {
    await this.api.post(`/workflows/${id}/activate`);
  }

  async deactivateWorkflow(id: number): Promise<void> {
    await this.api.post(`/workflows/${id}/deactivate`);
  }

  // Activity endpoints
  async getActivities(workflowId?: number): Promise<Activity[]> {
    const url = workflowId
      ? `/activities?workflowId=${workflowId}`
      : "/activities";
    const response: AxiosResponse<ApiResponse<Activity[]>> = await this.api.get(
      url
    );
    return response.data.data;
  }

  // Workflow execution endpoints
  async executeWorkflow(
    workflowId: number,
    initialData?: Record<string, any>
  ): Promise<WorkflowExecution> {
    const response: AxiosResponse<ApiResponse<WorkflowExecution>> =
      await this.api.post("/executions/start", {
        workflowId,
        initialData,
      });
    return response.data.data;
  }

  async startWorkflow(id: number): Promise<Workflow> {
    const response: AxiosResponse<ApiResponse<Workflow>> = await this.api.post(
      `/workflows/${id}/start`
    );
    return response.data.data;
  }

  async startWorkflowWithForm(
    id: number,
    workflowContainerFormData: Record<string, any>,
    projectMembers?: number[],
    workflowContainerId?: number,
    selectedPaftaStartNodes?: string[],
    allowedAdHocActivityIds?: number[]
  ): Promise<Workflow> {
    const requestBody: any = {
      workflowContainerFormData,
      projectMembers: projectMembers || [],
    };

    // Add workflowContainerId if provided (for new projects)
    if (workflowContainerId && workflowContainerId > 0) {
      requestBody.workflowContainerId = workflowContainerId;
    }
    if (selectedPaftaStartNodes !== undefined) {
      requestBody.selectedPaftaStartNodes = selectedPaftaStartNodes;
    }
    if (allowedAdHocActivityIds !== undefined) {
      requestBody.allowedAdHocActivityIds = allowedAdHocActivityIds;
    }

    const response: AxiosResponse<ApiResponse<Workflow>> = await this.api.post(
      `/workflows/${id}/start-with-form`,
      requestBody
    );
    return response.data.data;
  }

  async getLastWorkflowFormData(
    projectId: number
  ): Promise<{ data: Record<string, any>; formElements: any[] }> {
    const response: AxiosResponse<{
      data: Record<string, any>;
      formElements: any[];
      message: string;
      success: boolean;
    }> = await this.api.get(
      `/workflow-containers/${projectId}/last-workflow-form-data`
    );
    return {
      data: response.data.data,
      formElements: response.data.formElements,
    };
  }

  async getWorkflowExecutions(
    workflowId?: number
  ): Promise<WorkflowExecution[]> {
    const url = workflowId
      ? `/executions?workflowId=${workflowId}`
      : "/executions";
    const response: AxiosResponse<ApiResponse<WorkflowExecution[]>> =
      await this.api.get(url);
    return response.data.data;
  }

  async getExecutionById(id: number): Promise<WorkflowExecution> {
    const response: AxiosResponse<ApiResponse<WorkflowExecution>> =
      await this.api.get(`/executions/${id}`);
    return response.data.data;
  }

  // Task Form endpoints
  async submitUserSpecificFormData(
    activityInstanceId: number,
    formData: Record<string, any>,
    duration: number,
    startTime?: string,
    endTime?: string,
    breaks?: Array<{ start: string; end: string }>
  ): Promise<any> {
    const payload: Record<string, any> = {
      formData,
      duration,
      startTime,
      endTime,
    };
    if (breaks !== undefined) {
      payload.breaks = breaks;
    }
    const response: AxiosResponse<any> = await this.api.post(
      `/workflows/activity-instances/${activityInstanceId}/submit-user-specific`,
      payload
    );
    return response.data;
  }

  // WorkflowContainer endpoints
  async getAllWorkflowContainers(): Promise<WorkflowContainer[]> {
    const pageSize = 200;
    let page = 0;
    let hasNext = true;
    const allProjects: WorkflowContainer[] = [];

    while (hasNext) {
      const response: AxiosResponse<any> = await this.api.get(
        `/workflow-containers?page=${page}&size=${pageSize}`
      );
      const projects = response.data.projects || response.data.data || [];
      allProjects.push(...projects);

      if (response.data.hasNext === undefined) {
        break;
      }

      if (!response.data.hasNext || projects.length === 0) {
        hasNext = false;
      } else {
        page += 1;
      }
    }

    return allProjects;
  }

  async getWorkflowContainerById(id: number): Promise<WorkflowContainer> {
    const response: AxiosResponse<ApiResponse<WorkflowContainer>> =
      await this.api.get(`/workflow-containers/${id}`);
    return response.data.data;
  }

  async createWorkflowContainer(
    workflowContainerData: Partial<WorkflowContainer>
  ): Promise<WorkflowContainer> {
    const response: AxiosResponse<ApiResponse<WorkflowContainer>> =
      await this.api.post("/workflow-containers", workflowContainerData);
    return response.data.data;
  }

  async updateWorkflowContainer(
    id: number,
    workflowContainerData: Partial<WorkflowContainer>
  ): Promise<WorkflowContainer> {
    const response: AxiosResponse<ApiResponse<WorkflowContainer>> =
      await this.api.put(`/workflow-containers/${id}`, workflowContainerData);
    return response.data.data;
  }

  async updateWorkflowContainerFormData(
    id: number,
    formData: Record<string, any>
  ): Promise<void> {
    await this.api.put(`/workflow-containers/${id}/form-data`, { formData });
  }

  async getWorkflowContainerValueSuggestions(
    workflowContainerId: number,
    elementId?: string
  ): Promise<WorkflowFormValueSuggestion[]> {
    const query = elementId
      ? `?elementId=${encodeURIComponent(elementId)}`
      : "";
    const response: AxiosResponse<ApiResponse<WorkflowFormValueSuggestion[]>> =
      await this.api.get(
        `/workflow-containers/${workflowContainerId}/value-suggestions${query}`
      );
    return response.data.data || [];
  }

  async deleteWorkflowContainerValueSuggestion(
    workflowContainerId: number,
    suggestionId: number
  ): Promise<void> {
    await this.api.delete(
      `/workflow-containers/${workflowContainerId}/value-suggestions/${suggestionId}`
    );
  }

  // Get workflow instance form data (works even if project is soft-deleted)
  async getWorkflowInstanceFormData(workflowInstanceId: number): Promise<{
    formData: Record<string, any>;
    formElements: any[];
    projectMembers: number[];
    workflowContainerId: number;
    selectedPaftaStartNodes?: string[] | null;
    allowedAdHocActivityIds?: number[] | null;
    activitySelectionConfigured?: boolean;
  }> {
    const response = await this.api.get(
      `/workflows/workflow-instances/${workflowInstanceId}/form-data`
    );
    return response.data;
  }

  // Update workflow instance form data (works even if project is soft-deleted)
  async updateWorkflowInstanceFormData(
    workflowInstanceId: number,
    formData: Record<string, any>,
    projectMembers?: number[],
    selectedPaftaStartNodes?: string[],
    allowedAdHocActivityIds?: number[]
  ): Promise<void> {
    await this.api.put(
      `/workflows/workflow-instances/${workflowInstanceId}/form-data`,
      {
        formData,
        projectMembers,
        selectedPaftaStartNodes,
        allowedAdHocActivityIds,
      }
    );
  }

  // Change workflow instance template (project type) without deleting old activities
  async changeWorkflowInstanceTemplate(
    workflowInstanceId: number,
    templateWorkflowId: number,
    formData: Record<string, any>,
    projectMembers: number[],
    selectedPaftaStartNodes: string[],
    allowedAdHocActivityIds: number[]
  ): Promise<any> {
    const response = await this.api.put(
      `/workflows/workflow-instances/${workflowInstanceId}/change-template`,
      {
        templateWorkflowId,
        formData,
        projectMembers,
        selectedPaftaStartNodes,
        allowedAdHocActivityIds,
      }
    );
    return response.data.data;
  }

  async deleteWorkflowContainer(id: number): Promise<void> {
    await this.api.delete(`/workflow-containers/${id}`);
  }

  async getWorkflowContainersByTeamMember(): Promise<WorkflowContainer[]> {
    const response: AxiosResponse<ApiResponse<WorkflowContainer[]>> =
      await this.api.get("/workflow-containers/my-projects");
    return response.data.data;
  }

  // WorkflowContainer Member endpoints
  async getWorkflowContainerMembers(
    workflowContainerId: number
  ): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(
      `/workflow-container-members?workflowContainerId=${workflowContainerId}`
    );
    return response.data.data;
  }

  // Get workflow container members (enriched with user data)
  async getWorkflowContainerMembersEnriched(
    workflowContainerId: number
  ): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(
      `/workflow-containers/${workflowContainerId}/members`
    );
    return response.data.data;
  }

  async addWorkflowContainerMember(
    workflowContainerId: number,
    userId: number,
    role: string,
    canStartWorkflow?: boolean
  ): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/workflow-container-members`,
      {
        workflowContainerId,
        userId,
        role,
        canStartWorkflow: canStartWorkflow || false,
      }
    );
    return response.data.data;
  }

  async removeWorkflowContainerMember(
    workflowContainerId: number,
    memberId: number
  ): Promise<void> {
    await this.api.delete(`/workflow-container-members/${memberId}`);
  }

  async updateWorkflowContainerMember(
    workflowContainerId: number,
    memberId: number,
    data: { role: string; canStartWorkflow: boolean }
  ): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(
      `/workflow-container-members/${memberId}`,
      data
    );
    return response.data.data;
  }

  // Activity endpoints
  async getAllActivities(params?: {
    search?: string;
    tag?: string;
    onlyMine?: boolean;
    activeOnly?: boolean;
  }): Promise<ActivityDefinition[]> {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.append("search", params.search);
    if (params?.tag) searchParams.append("tag", params.tag);
    if (params?.onlyMine) searchParams.append("onlyMine", "true");
    if (params?.activeOnly) searchParams.append("activeOnly", "true");

    const response: AxiosResponse<ApiResponse<ActivityDefinition[]>> =
      await this.api.get(`/activities?${searchParams}`);
    return response.data.data;
  }

  async getActiveActivities(): Promise<ActivityDefinition[]> {
    return this.getAllActivities({ activeOnly: true });
  }

  async getActivityById(id: number): Promise<ActivityDefinition> {
    const response: AxiosResponse<ApiResponse<ActivityDefinition>> =
      await this.api.get(`/activities/${id}`);
    return response.data.data;
  }

  async createActivity(
    activityData: Partial<ActivityDefinition>
  ): Promise<ActivityDefinition> {
    const response: AxiosResponse<ApiResponse<ActivityDefinition>> =
      await this.api.post("/activities", activityData);
    return response.data.data;
  }

  async updateActivity(
    id: number,
    activityData: Partial<ActivityDefinition>
  ): Promise<ActivityDefinition> {
    const response: AxiosResponse<ApiResponse<ActivityDefinition>> =
      await this.api.put(`/activities/${id}`, activityData);
    return response.data.data;
  }

  async deleteActivity(id: number): Promise<void> {
    await this.api.delete(`/activities/${id}`);
  }

  async duplicateActivity(id: number): Promise<ActivityDefinition> {
    const response: AxiosResponse<ApiResponse<ActivityDefinition>> =
      await this.api.post(`/activities/${id}/duplicate`);
    return response.data.data;
  }

  async getAssignedActivities(): Promise<ActivityDefinition[]> {
    const response: AxiosResponse<ApiResponse<ActivityDefinition[]>> =
      await this.api.get("/activities/assigned-to-me");
    return response.data.data;
  }

  async addFormToActivity(
    activityId: number,
    formId: number,
    displayOrder?: number
  ): Promise<void> {
    const params = displayOrder ? `?displayOrder=${displayOrder}` : "";
    await this.api.post(`/activities/${activityId}/forms/${formId}${params}`);
  }

  async removeFormFromActivity(
    activityId: number,
    formId: number
  ): Promise<void> {
    await this.api.delete(`/activities/${activityId}/forms/${formId}`);
  }

  async clearActivityForms(activityId: number): Promise<void> {
    await this.api.delete(`/activities/${activityId}/forms`);
  }

  // Form endpoints
  async getAllForms(params?: {
    search?: string;
    tag?: string;
    onlyMine?: boolean;
  }): Promise<Form[]> {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.append("search", params.search);
    if (params?.tag) searchParams.append("tag", params.tag);
    if (params?.onlyMine) searchParams.append("onlyMine", "true");

    const response: AxiosResponse<ApiResponse<Form[]>> = await this.api.get(
      `/forms?${searchParams}`
    );
    return response.data.data;
  }

  async getFormById(id: number): Promise<Form> {
    const response: AxiosResponse<ApiResponse<Form>> = await this.api.get(
      `/forms/${id}`
    );
    return response.data.data;
  }

  async createForm(formData: Partial<Form>): Promise<Form> {
    const response: AxiosResponse<ApiResponse<Form>> = await this.api.post(
      "/forms",
      formData
    );
    return response.data.data;
  }

  async updateForm(id: number, formData: Partial<Form>): Promise<Form> {
    const response: AxiosResponse<ApiResponse<Form>> = await this.api.put(
      `/forms/${id}`,
      formData
    );
    return response.data.data;
  }

  async deleteForm(id: number): Promise<void> {
    await this.api.delete(`/forms/${id}`);
  }

  async duplicateForm(id: number): Promise<Form> {
    const response: AxiosResponse<ApiResponse<Form>> = await this.api.post(
      `/forms/${id}/duplicate`
    );
    return response.data.data;
  }

  // Project endpoints
  async updateProject(id: number, projectData: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put(
      `/workflow-containers/${id}`,
      projectData
    );
    return response.data;
  }

  // Workflow Template endpoints
  async getAllWorkflowTemplates(params?: {
    search?: string;
    category?: string;
    onlyMine?: boolean;
  }): Promise<any[]> {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.append("search", params.search);
    if (params?.category) searchParams.append("category", params.category);
    if (params?.onlyMine)
      searchParams.append("onlyMine", params.onlyMine.toString());

    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(
      `/workflow-templates?${searchParams.toString()}`
    );
    return response.data.data;
  }

  async getWorkflowTemplateById(id: number): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(
      `/workflow-templates/${id}`
    );
    return response.data.data;
  }

  async createWorkflowTemplate(templateData: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      "/workflow-templates",
      templateData
    );
    return response.data.data;
  }

  async updateWorkflowTemplate(id: number, templateData: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(
      `/workflow-templates/${id}`,
      templateData
    );
    return response.data.data;
  }

  async deleteWorkflowTemplate(id: number): Promise<void> {
    await this.api.delete(`/workflow-templates/${id}`);
  }

  // Employee accessible workflows
  async getAccessibleWorkflows(): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(
      "/workflows/accessible"
    );
    return response.data.data;
  }

  // Workflow instances
  async getWorkflowInstances(): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(
      "/workflows/instances"
    );
    return response.data.data;
  }

  async getWorkflowInstanceById(instanceId: number): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(
      `/workflows/instances/${instanceId}`
    );
    return response.data.data;
  }

  // Complete workflow instance (mark as COMPLETED)
  async completeWorkflowInstance(
    instanceId: number,
    payload?: { archivedFinancialSnapshot?: Record<string, any> }
  ): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(
      `/workflows/instances/${instanceId}/complete`,
      payload || {}
    );
    return response.data.data;
  }

  // Reactivate workflow instance (mark as IN_PROGRESS)
  async reactivateWorkflowInstance(instanceId: number): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(
      `/workflows/instances/${instanceId}/reactivate`
    );
    return response.data.data;
  }

  async getDailyHoursStatus(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(
      "/workflows/daily-hours"
    );
    return response.data;
  }

  async getCompanySettings(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(
      "/company-settings"
    );
    return response.data;
  }

  async updateWorkHoursSettings(
    dailyTargetHours: number,
    overtimeMultiplier: number
  ): Promise<any> {
    const payload = {
      dailyTargetHours,
      overtimeMultiplier,
    };
    const response: AxiosResponse<any> = await this.api.put(
      "/company-settings/work-hours",
      payload
    );
    return response.data;
  }

  async updateWorkCalendarSettings(
    year: number,
    holidays: string[],
    workdayOverrides: string[],
    halfDays: string[]
  ): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put(
      "/company-settings/work-calendar",
      { year, holidays, workdayOverrides, halfDays }
    );
    return response.data;
  }

  async updateTaxSettings(
    vatRate: number,
    corporateTaxRate: number
  ): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put(
      "/company-settings/taxes",
      { vatRate, corporateTaxRate }
    );
    return response.data;
  }

  async updateActivityTableLayout(
    screenKey: string,
    columns: Array<{ id: string; kind: string; label: string }>,
    defaultVersion?: number,
  ): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put(
      `/company-settings/activity-table-layouts/${screenKey}`,
      {
        columns,
        ...(typeof defaultVersion === "number" ? { defaultVersion } : {}),
      },
    );
    return response.data;
  }

  async updateWhatsAppReportBotSettings(payload: {
    enabled: boolean;
    recipients: string[];
    sendTime: string;
    selectedActivityColumns: string[];
  }): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put(
      "/company-settings/whatsapp-report-bot",
      payload
    );
    return response.data;
  }

  async resendWhatsAppReportNow(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(
      "/company-settings/whatsapp-report-bot/resend"
    );
    return response.data;
  }

  async sendWhatsAppTestMessage(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(
      "/company-settings/whatsapp-report-bot/test-message"
    );
    return response.data;
  }

  async getHourlyCosts(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(
      "/company-settings/hourly-costs"
    );
    return response.data;
  }

  async updateHourlyCost(
    userId: number,
    hourlyCost: number | null,
    currency?: string | null
  ): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put(
      `/company-settings/hourly-costs/${userId}`,
      { hourlyCost, currency }
    );
    return response.data;
  }

  async getHourlyCostRates(userId?: number): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(
      "/company-settings/hourly-cost-rates",
      { params: userId ? { userId } : {} }
    );
    return response.data;
  }

  async createHourlyCostRate(
    userId: number,
    payload: {
      startDate: string;
      endDate?: string | null;
      hourlyCost: number | null;
      currency?: string | null;
    }
  ): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(
      `/company-settings/hourly-cost-rates/${userId}`,
      payload
    );
    return response.data;
  }

  async updateHourlyCostRate(
    rateId: number,
    payload: {
      startDate: string;
      endDate?: string | null;
      hourlyCost: number | null;
      currency?: string | null;
    }
  ): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put(
      `/company-settings/hourly-cost-rates/${rateId}`,
      payload
    );
    return response.data;
  }

  async deleteHourlyCostRate(rateId: number): Promise<any> {
    const response: AxiosResponse<any> = await this.api.delete(
      `/company-settings/hourly-cost-rates/${rateId}`
    );
    return response.data;
  }

  async getAverageHourlyCostRates(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(
      "/company-settings/average-hourly-cost-rates"
    );
    return response.data;
  }

  async createAverageHourlyCostRate(payload: {
    startDate: string;
    endDate?: string | null;
    hourlyCost: number | null;
    currency?: string | null;
  }): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(
      "/company-settings/average-hourly-cost-rates",
      payload
    );
    return response.data;
  }

  async updateAverageHourlyCostRate(
    rateId: number,
    payload: {
      startDate: string;
      endDate?: string | null;
      hourlyCost: number | null;
      currency?: string | null;
    }
  ): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put(
      `/company-settings/average-hourly-cost-rates/${rateId}`,
      payload
    );
    return response.data;
  }

  async deleteAverageHourlyCostRate(rateId: number): Promise<any> {
    const response: AxiosResponse<any> = await this.api.delete(
      `/company-settings/average-hourly-cost-rates/${rateId}`
    );
    return response.data;
  }

  async getLatestExchangeRates(base: string = "TRY"): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(
      "/exchange-rates/latest",
      { params: { base } }
    );
    return response.data;
  }

  async getDailyExchangeRates(
    startDate?: string,
    endDate?: string,
    base: string = "TRY"
  ): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(
      "/exchange-rates/daily",
      {
        params: {
          base,
          startDate,
          endDate,
        },
      }
    );
    return response.data;
  }

  async upsertDailyExchangeRate(
    rateDate: string,
    rates: Record<string, number | string | null | undefined>,
    base: string = "TRY"
  ): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put(
      `/exchange-rates/daily/${encodeURIComponent(rateDate)}`,
      { rates },
      {
        params: { base },
      }
    );
    return response.data;
  }

  async deleteDailyExchangeRate(
    rateDate: string,
    base: string = "TRY"
  ): Promise<any> {
    const response: AxiosResponse<any> = await this.api.delete(
      `/exchange-rates/daily/${encodeURIComponent(rateDate)}`,
      {
        params: { base },
      }
    );
    return response.data;
  }

  async convertCurrency(
    from: string,
    to: string,
    amount: number
  ): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(
      "/exchange-rates/convert",
      { params: { from, to, amount } }
    );
    return response.data;
  }

  // Activity manager comments (admin/manager -> activity owner notifications)
  async createActivityManagerComment(payload: {
    activityInstanceId: number;
    targetUserId?: number | null;
    comment: string;
    isGlobal?: boolean;
  }): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(
      "/activity-manager-comments",
      payload
    );
    return response.data;
  }

  async getUnreadActivityManagerComments(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(
      "/activity-manager-comments/unread"
    );
    return response.data;
  }

  async getActivityManagerComments(
    activityInstanceId: number,
    targetUserId: number
  ): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(
      "/activity-manager-comments",
      { params: { activityInstanceId, targetUserId } }
    );
    return response.data;
  }

  async markActivityManagerCommentRead(commentId: number): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put(
      `/activity-manager-comments/${commentId}/read`
    );
    return response.data;
  }

  async getLatestActivityManagerComments(payload: {
    pairs?: Array<{ activityInstanceId: number; targetUserId: number }>;
    activityInstanceIds?: number[];
  }): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(
      "/activity-manager-comments/latest",
      payload
    );
    return response.data;
  }

  // Activity instances
  async getActivityInstances(): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(
      "/workflows/activity-instances"
    );
    return response.data.data;
  }

  // Get activity instances by workflow instance ID (includes all statuses)
  async getActivityInstancesByWorkflowInstance(
    workflowInstanceId: number
  ): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(
      `/workflows/workflow-instances/${workflowInstanceId}/activities`
    );
    return response.data.data;
  }

  async previewProjectFolderName(
    id: number,
    workflowContainerFormData: Record<string, any>,
    workflowContainerId?: number
  ): Promise<{ folderName?: string; projectCode?: string; projectName?: string }> {
    const requestBody: any = {
      workflowContainerFormData: workflowContainerFormData || {},
    };

    if (workflowContainerId && workflowContainerId > 0) {
      requestBody.workflowContainerId = workflowContainerId;
    }

    const response: AxiosResponse<
      ApiResponse<{ folderName?: string; projectCode?: string; projectName?: string }>
    > = await this.api.post(`/workflows/${id}/project-folder-preview`, requestBody);
    return response.data.data || {};
  }

  // Get limited user list for a workflow instance (participants). Used for VISITOR role.
  async getWorkflowInstancePeople(workflowInstanceId: number): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(
      `/workflows/workflow-instances/${workflowInstanceId}/people`
    );
    return response.data.data;
  }

  // Start activity instance
  async startActivityInstance(activityInstanceId: number): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/workflows/activity-instances/${activityInstanceId}/start`
    );
    return response.data.data;
  }

  // Complete activity instance
  async completeActivityInstance(
    activityInstanceId: number,
    submitDescription?: string,
    attachments?: GoogleDriveAttachment[]
  ): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/workflows/activity-instances/${activityInstanceId}/complete`,
      { submitDescription, attachments }
    );
    return response.data.data;
  }

  // Approve activity instance (manager/admin)
  async approveActivityInstance(activityInstanceId: number): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/workflows/activity-instances/${activityInstanceId}/approve`
    );
    return response.data.data;
  }

  // Reject activity instance (manager/admin)
  async rejectActivityInstance(activityInstanceId: number): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/workflows/activity-instances/${activityInstanceId}/reject`
    );
    return response.data.data;
  }

  // Update activity duration
  async updateActivityDuration(
    activityInstanceId: number,
    duration: number
  ): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(
      `/workflows/activity-instances/${activityInstanceId}/duration`,
      { duration }
    );
    return response.data.data;
  }

  // Update activity history (user-specific edit)
  async updateActivityHistory(
    activityInstanceId: number,
    payload: {
      formData: any;
      duration: number;
      startTime?: string;
      endTime?: string;
      breaks?: Array<{ start: string; end: string }>;
      submitDescription?: string;
      attachments?: GoogleDriveAttachment[];
      removeAttachmentIds?: string[];
    }
  ): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(
      `/workflows/activity-instances/${activityInstanceId}/history`,
      payload
    );
    return response.data;
  }

  // Soft delete activity instance
  async deleteActivityInstance(activityInstanceId: number): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(
      `/workflows/activity-instances/${activityInstanceId}/delete`
    );
    return response.data.data;
  }

  // Save form data for activity instance
  async saveActivityFormData(
    activityInstanceId: number,
    formData: any
  ): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/workflows/activity-instances/${activityInstanceId}/form-data`,
      { formData }
    );
    return response.data.data;
  }

  // Submit form instance with submission data
  async submitFormInstance(
    formInstanceId: number,
    submissionData: any
  ): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/workflows/form-instances/${formInstanceId}/submit`,
      { submissionData }
    );
    return response.data.data;
  }

  // ✅ NEW: Get activity instance with ALL users' form responses (for managers)
  async getActivityInstanceWithAllUserForms(
    activityInstanceId: number
  ): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(
      `/workflows/activity-instances/${activityInstanceId}/all-user-forms`
    );
    return response.data.data;
  }

  // Activity Reports endpoints
  async getActivityReports(
    filters?: ActivityReportFilters
  ): Promise<ActivityReport[]> {
    const params = new URLSearchParams();

    if (filters?.workflowContainerId) {
      params.append(
        "workflowContainerId",
        filters.workflowContainerId.toString()
      );
    }
    if (filters?.status) {
      params.append("status", filters.status);
    }
    if (filters?.assigneeId) {
      params.append("assigneeId", filters.assigneeId.toString());
    }
    if (filters?.startDate) {
      params.append("startDate", filters.startDate);
    }
    if (filters?.endDate) {
      params.append("endDate", filters.endDate);
    }

    const response: AxiosResponse<ActivityReport[]> = await this.api.get(
      `/activity-reports?${params.toString()}`
    );
    return response.data;
  }

  async getActivityReportsList(
    filters?: ActivityReportFilters & { page?: number; size?: number }
  ): Promise<ActivityReportListResponse> {
    const params = new URLSearchParams();

    if (filters?.tab) {
      params.append("tab", filters.tab);
    }
    if (filters?.searchTerm) {
      params.append("searchTerm", filters.searchTerm);
    }
    if (filters?.workflowContainerId) {
      params.append(
        "workflowContainerId",
        filters.workflowContainerId.toString()
      );
    }
    if (filters?.workflowInstanceId) {
      params.append(
        "workflowInstanceId",
        filters.workflowInstanceId.toString()
      );
    }
    if (filters?.workflowContainerName) {
      params.append("workflowContainerName", filters.workflowContainerName);
    }
    if (filters?.workflowName) {
      params.append("workflowName", filters.workflowName);
    }
    if (filters?.activityTitle) {
      params.append("activityTitle", filters.activityTitle);
    }
    if (filters?.status) {
      params.append("status", filters.status);
    }
    if (filters?.assigneeId) {
      params.append("assigneeId", filters.assigneeId.toString());
    }
    if (filters?.startDate) {
      params.append("startDate", filters.startDate);
    }
    if (filters?.endDate) {
      params.append("endDate", filters.endDate);
    }
    params.append("page", String(filters?.page ?? 0));
    params.append("size", String(filters?.size ?? 1000));

    const response: AxiosResponse<ActivityReportListResponse> = await this.api.get(
      `/activity-reports/list?${params.toString()}`
    );
    return response.data;
  }

  async getActivityReportDetail(activityInstanceId: number): Promise<ActivityReport> {
    const response: AxiosResponse<ActivityReport> = await this.api.get(
      `/activity-reports/${activityInstanceId}`
    );
    return response.data;
  }

  async updateActivityInvoiceStatus(
    activityInstanceId: number,
    isInvoiced: boolean
  ): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(
      `/workflows/activity-instances/${activityInstanceId}/invoice-status`,
      { isInvoiced }
    );
    return response.data.data;
  }

  async getWorkflowContainersForManager(): Promise<WorkflowContainer[]> {
    const response: AxiosResponse<WorkflowContainer[]> = await this.api.get(
      "/workflow-containers/my-projects"
    );
    return response.data;
  }

  // Google Drive Methods
  async getGoogleDriveAuthStatus(): Promise<{
    status: string;
    authorized: boolean;
    hasStoredCredentials?: boolean;
  }> {
    const response = await this.api.get("/google-drive/auth/status");
    return response.data;
  }

  async resetGoogleDriveAuth(): Promise<{ message: string }> {
    const response = await this.api.post("/google-drive/auth/reset");
    return response.data;
  }

  async getGoogleDriveRootFolder(): Promise<{ rootFolderId?: string }> {
    const response: AxiosResponse<{ rootFolderId?: string }> = await this.api.get(
      "/google-drive/root-folder"
    );
    return response.data;
  }

  async updateGoogleDriveRootFolder(
    rootFolderId: string
  ): Promise<{ rootFolderId?: string }> {
    const response: AxiosResponse<{ rootFolderId?: string }> = await this.api.put(
      "/google-drive/root-folder",
      { rootFolderId }
    );
    return response.data;
  }

  async listGoogleDriveFiles(
    folderId?: string
  ): Promise<GoogleDriveAttachment[]> {
    const params = folderId ? `?folderId=${folderId}` : "";
    const response: AxiosResponse<GoogleDriveAttachment[]> = await this.api.get(
      `/google-drive/files${params}`
    );
    return response.data;
  }

  /**
   * Get workflow instance files with metadata (description, uploadedAt, uploadedBy)
   * @param workflowInstanceId The workflow instance ID
   * @returns Files with merged metadata from database
   */
  async getWorkflowInstanceFiles(
    workflowInstanceId: number,
    folderId?: string | null
  ): Promise<GoogleDriveAttachment[]> {
    const params = folderId ? `?folderId=${encodeURIComponent(folderId)}` : "";
    const response = await this.api.get(
      `/workflows/instances/${workflowInstanceId}/files${params}`
    );
    return response.data.files;
  }

  private async listFilesForUploadVerification(
    workflowInstanceId: number,
    targetFolderId?: string | null,
    subfolderName?: string
  ): Promise<GoogleDriveAttachment[]> {
    try {
      const normalizedTargetFolderId = targetFolderId?.trim();
      if (normalizedTargetFolderId) {
        return await this.getWorkflowInstanceFiles(
          workflowInstanceId,
          normalizedTargetFolderId
        );
      }

      const normalizedSubfolderName = subfolderName?.trim();
      if (normalizedSubfolderName) {
        const rootFiles = await this.getWorkflowInstanceFiles(workflowInstanceId);
        const matchingSubfolder = rootFiles.find(
          (file) =>
            file.mimeType === "application/vnd.google-apps.folder" &&
            file.name?.trim() === normalizedSubfolderName
        );

        if (!matchingSubfolder?.id) {
          return [];
        }

        return await this.getWorkflowInstanceFiles(
          workflowInstanceId,
          matchingSubfolder.id
        );
      }

      return await this.getWorkflowInstanceFiles(workflowInstanceId);
    } catch (verificationError) {
      return [];
    }
  }

  private async detectRecoveredProjectUpload(
    workflowInstanceId: number,
    fileName: string,
    knownFileIds: Set<string>,
    targetFolderId?: string | null,
    subfolderName?: string
  ): Promise<GoogleDriveAttachment | null> {
    for (let attempt = 0; attempt < 2; attempt++) {
      const files = await this.listFilesForUploadVerification(
        workflowInstanceId,
        targetFolderId,
        subfolderName
      );
      const recoveredFile =
        files.find(
          (file) =>
            file.name === fileName &&
            typeof file.id === "string" &&
            !knownFileIds.has(file.id)
        ) || null;

      if (recoveredFile) {
        return recoveredFile;
      }

      if (attempt === 0) {
        await new Promise((resolve) => window.setTimeout(resolve, 800));
      }
    }

    return null;
  }

  async deleteWorkflowInstanceFile(
    workflowInstanceId: number,
    fileId: string
  ): Promise<void> {
    await this.api.delete(
      `/workflows/instances/${workflowInstanceId}/files/${fileId}`
    );
  }

  async deleteWorkflowInstanceFolder(
    workflowInstanceId: number,
    folderId: string
  ): Promise<void> {
    await this.api.delete(
      `/workflows/instances/${workflowInstanceId}/folders/${folderId}`
    );
  }

  async downloadWorkflowInstanceFolder(
    workflowInstanceId: number,
    folderId: string,
    fallbackFileName?: string
  ): Promise<void> {
    const response = await this.api.get(
      `/workflows/instances/${workflowInstanceId}/folders/${encodeURIComponent(folderId)}/download`,
      {
        responseType: "blob",
      }
    );

    const contentDisposition =
      response.headers["content-disposition"] ||
      response.headers["Content-Disposition"];
    const fileName =
      this.extractDownloadFilename(contentDisposition) ||
      fallbackFileName ||
      "folder.zip";

    const blobUrl = window.URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  }

  async downloadWorkflowInstanceFile(
    workflowInstanceId: number,
    fileId: string,
    fallbackFileName?: string
  ): Promise<void> {
    const response = await this.api.get(
      `/workflows/instances/${workflowInstanceId}/files/${encodeURIComponent(fileId)}/download`,
      {
        responseType: "blob",
      }
    );

    const contentDisposition =
      response.headers["content-disposition"] ||
      response.headers["Content-Disposition"];
    const fileName =
      this.extractDownloadFilename(contentDisposition) ||
      fallbackFileName ||
      "download";

    const blobUrl = window.URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  }

  async previewWorkflowInstanceFile(
    workflowInstanceId: number,
    fileId: string,
    fallbackFileName?: string,
    targetWindow?: Window | null
  ): Promise<void> {
    const response = await this.api.get(
      `/workflows/instances/${workflowInstanceId}/files/${encodeURIComponent(fileId)}/preview`,
      {
        responseType: "blob",
      }
    );

    const contentDisposition =
      response.headers["content-disposition"] ||
      response.headers["Content-Disposition"];
    const fileName =
      this.extractDownloadFilename(contentDisposition) ||
      fallbackFileName ||
      "preview";
    const contentType =
      response.headers["content-type"] ||
      response.headers["Content-Type"] ||
      "application/octet-stream";

    const blob = new Blob([response.data], { type: contentType });
    const blobUrl = window.URL.createObjectURL(blob);

    if (!this.isBrowserPreviewableContentType(contentType, fileName)) {
      if (targetWindow && !targetWindow.closed) {
        targetWindow.close();
      }
      this.downloadBlobUrl(blobUrl, fileName);
      window.URL.revokeObjectURL(blobUrl);
      return;
    }

    if (targetWindow && !targetWindow.closed) {
      targetWindow.location.href = blobUrl;
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
      return;
    }

    const previewWindow = window.open(blobUrl, "_blank", "noopener,noreferrer");
    if (previewWindow) {
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
      return;
    }

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  }

  async previewGoogleDriveFile(
    fileId: string,
    fallbackFileName?: string,
    targetWindow?: Window | null
  ): Promise<void> {
    const response = await this.api.get(
      `/google-drive/files/${encodeURIComponent(fileId)}/preview`,
      {
        responseType: "blob",
      }
    );

    const contentDisposition =
      response.headers["content-disposition"] ||
      response.headers["Content-Disposition"];
    const fileName =
      this.extractDownloadFilename(contentDisposition) ||
      fallbackFileName ||
      "preview";
    const contentType =
      response.headers["content-type"] ||
      response.headers["Content-Type"] ||
      "application/octet-stream";

    const blob = new Blob([response.data], { type: contentType });
    const blobUrl = window.URL.createObjectURL(blob);

    if (!this.isBrowserPreviewableContentType(contentType, fileName)) {
      if (targetWindow && !targetWindow.closed) {
        targetWindow.close();
      }
      this.downloadBlobUrl(blobUrl, fileName);
      window.URL.revokeObjectURL(blobUrl);
      return;
    }

    if (targetWindow && !targetWindow.closed) {
      targetWindow.location.href = blobUrl;
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
      return;
    }

    const previewWindow = window.open(blobUrl, "_blank", "noopener,noreferrer");
    if (previewWindow) {
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
      return;
    }

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  }

  shouldOpenPreviewWindow(fileName?: string, contentType?: string): boolean {
    return this.isBrowserPreviewableContentType(contentType, fileName);
  }

  preparePreviewWindow(previewWindow: Window | null, fileName?: string): void {
    if (!previewWindow || previewWindow.closed) {
      return;
    }

    const title = "Dosya hazırlanıyor";
    const safeFileName = this.escapeHtml(fileName || "Dosya");
    previewWindow.document.open();
    previewWindow.document.write(`<!doctype html>
<html lang="tr">
  <head>
    <meta charset="utf-8" />
    <title>${title}</title>
    <style>
        html,
        body {
          height: 100%;
          margin: 0;
          font-family: Arial, sans-serif;
          background: #f8fafc;
          color: #0f172a;
        }
        .preview-loader {
          min-height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          gap: 16px;
        }
        .preview-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #dbeafe;
          border-top-color: #2563eb;
          border-radius: 9999px;
          animation: preview-spin 0.8s linear infinite;
        }
        .preview-title {
          font-size: 16px;
          font-weight: 700;
        }
        .preview-file {
          max-width: min(560px, calc(100vw - 40px));
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: #64748b;
          font-size: 13px;
        }
        @keyframes preview-spin {
          to {
            transform: rotate(360deg);
          }
        }
    </style>
  </head>
  <body>
    <div class="preview-loader">
        <div class="preview-spinner"></div>
        <div class="preview-title">Dosya hazırlanıyor...</div>
      <div class="preview-file">${safeFileName}</div>
    </div>
  </body>
</html>`);
    previewWindow.document.close();
  }

  private isBrowserPreviewableContentType(
    contentType?: string,
    fileName?: string
  ): boolean {
    const normalized = (contentType || "").split(";")[0].trim().toLowerCase();
    const extension = (fileName || "")
      .split("?")[0]
      .split("#")[0]
      .toLowerCase()
      .match(/\.([a-z0-9]+)$/)?.[1];

    const forcedDownloadExtensions = new Set([
      "dwg",
      "dxf",
      "rar",
      "zip",
      "7z",
      "tar",
      "gz",
      "doc",
      "docx",
      "xls",
      "xlsx",
      "ppt",
      "pptx",
    ]);
    if (extension && forcedDownloadExtensions.has(extension)) {
      return false;
    }

    const previewableExtensions = new Set([
      "avif",
      "bmp",
      "csv",
      "gif",
      "htm",
      "html",
      "ico",
      "jpeg",
      "jpg",
      "json",
      "md",
      "mp3",
      "mp4",
      "mpeg",
      "ogg",
      "pdf",
      "png",
      "svg",
      "txt",
      "wav",
      "webm",
      "webp",
      "xml",
    ]);
    if (extension && previewableExtensions.has(extension)) {
      return true;
    }

    const previewableImageTypes = new Set([
      "image/avif",
      "image/bmp",
      "image/gif",
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/svg+xml",
      "image/webp",
      "image/x-icon",
      "image/vnd.microsoft.icon",
    ]);

    return (
      normalized === "application/pdf" ||
      normalized === "application/json" ||
      normalized === "application/xml" ||
      normalized === "text/xml" ||
      previewableImageTypes.has(normalized) ||
      normalized.startsWith("text/") ||
      normalized.startsWith("audio/") ||
      normalized.startsWith("video/")
    );
  }

  private downloadBlobUrl(blobUrl: string, fileName: string): void {
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  private escapeHtml(value: string): string {
    return value.replace(/[&<>"']/g, (char) => {
      switch (char) {
        case "&":
          return "&amp;";
        case "<":
          return "&lt;";
        case ">":
          return "&gt;";
        case '"':
          return "&quot;";
        case "'":
          return "&#039;";
        default:
          return char;
      }
    });
  }

  private extractDownloadFilename(contentDisposition?: string): string | null {
    if (!contentDisposition) {
      return null;
    }

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      try {
        return decodeURIComponent(utf8Match[1]);
      } catch {
        return utf8Match[1];
      }
    }

    const standardMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
    return standardMatch?.[1] || null;
  }

  async getProjectCollections(
    workflowInstanceId: number
  ): Promise<any[]> {
    const response = await this.api.get(
      `/workflows/instances/${workflowInstanceId}/collections`
    );
    return response.data.data || [];
  }

  async getProjectCollectionsForInstances(
    workflowInstanceIds: number[]
  ): Promise<any[]> {
    const uniqueIds = Array.from(
      new Set(
        workflowInstanceIds
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && id > 0),
      ),
    );

    if (!uniqueIds.length) {
      return [];
    }

    const chunkSize = 50;
    const chunks: number[][] = [];
    for (let index = 0; index < uniqueIds.length; index += chunkSize) {
      chunks.push(uniqueIds.slice(index, index + chunkSize));
    }

    const responses = await Promise.all(
      chunks.map((chunk) => {
        const params = chunk
          .map((id) => `workflowInstanceIds=${encodeURIComponent(String(id))}`)
          .join("&");
        return this.api.get(`/workflows/instances/collections?${params}`);
      }),
    );

    return responses.flatMap((response) => response.data.data || []);
  }

  async createProjectCollection(
    workflowInstanceId: number,
    payload: {
      collectionDate: string;
      amount: number;
      currency: string;
      description?: string;
      attachments?: any[];
    }
  ): Promise<any> {
    const response = await this.api.post(
      `/workflows/instances/${workflowInstanceId}/collections`,
      payload
    );
    return response.data.data;
  }

  async updateProjectCollection(
    workflowInstanceId: number,
    collectionId: number,
    payload: {
      collectionDate: string;
      amount: number;
      currency: string;
      description?: string;
      attachments?: any[];
    }
  ): Promise<any> {
    const response = await this.api.put(
      `/workflows/instances/${workflowInstanceId}/collections/${collectionId}`,
      payload
    );
    return response.data.data;
  }

  async deleteProjectCollection(
    workflowInstanceId: number,
    collectionId: number
  ): Promise<void> {
    await this.api.delete(
      `/workflows/instances/${workflowInstanceId}/collections/${collectionId}`
    );
  }

  async attachGoogleDriveFile(fileId: string): Promise<GoogleDriveAttachment> {
    const response: AxiosResponse<GoogleDriveAttachment> = await this.api.post(
      `/google-drive/attach?fileId=${fileId}`
    );
    return response.data;
  }

  async listGoogleDriveFolders(
    parentFolderId?: string
  ): Promise<GoogleDriveAttachment[]> {
    const params = parentFolderId ? `?parentFolderId=${parentFolderId}` : "";
    const response: AxiosResponse<GoogleDriveAttachment[]> = await this.api.get(
      `/google-drive/folders${params}`
    );
    return response.data;
  }

  async getGoogleDriveFileInfo(fileId: string): Promise<GoogleDriveAttachment> {
    const response: AxiosResponse<GoogleDriveAttachment> = await this.api.get(
      `/google-drive/file/${fileId}`
    );
    return response.data;
  }

  /**
   * Upload a file to a project's Google Drive folder
   * @param workflowInstanceId The workflow instance ID (project)
   * @param file The file to upload
   * @param description Optional description for the file
   * @returns Uploaded file information
   */
  async uploadFileToProject(
    workflowInstanceId: number,
    file: File,
    description?: string,
    subfolderName?: string,
    category?: string,
    targetFolderId?: string | null
  ): Promise<GoogleDriveAttachment> {
    const formData = new FormData();
    const sanitizedFileName =
      file.name.split(/[\\/]/).pop()?.trim() || file.name || "uploaded-file";
    formData.append("file", file, sanitizedFileName);
    formData.append("workflowInstanceId", workflowInstanceId.toString());
    if (description) {
      formData.append("description", description);
    }
    if (subfolderName) {
      formData.append("subfolderName", subfolderName);
    }
    if (category) {
      formData.append("category", category);
    }
    if (targetFolderId) {
      formData.append("targetFolderId", targetFolderId);
    }

    const knownFileIds = new Set(
      (
        await this.listFilesForUploadVerification(
          workflowInstanceId,
          targetFolderId,
          subfolderName
        )
      )
        .map((existingFile) => existingFile.id)
        .filter(
          (existingFileId): existingFileId is string =>
            typeof existingFileId === "string" && existingFileId.length > 0
        )
    );

    try {
      const response: AxiosResponse<GoogleDriveAttachment> = await this.api.post(
        `/google-drive/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (uploadError) {
      const recoveredUpload = await this.detectRecoveredProjectUpload(
        workflowInstanceId,
        file.name,
        knownFileIds,
        targetFolderId,
        subfolderName
      );

      if (recoveredUpload) {
        return recoveredUpload;
      }

      throw uploadError;
    }
  }

  /**
   * Copy a Google Drive file to a project's folder
   * @param workflowInstanceId The workflow instance ID (project)
   * @param fileId The Google Drive file ID to copy
   * @param description Optional description for the file
   * @returns Copied file information
   */
  async copyDriveFileToProject(
    workflowInstanceId: number,
    fileId: string,
    description?: string,
    targetFolderId?: string | null
  ): Promise<GoogleDriveAttachment> {
    const url = `/google-drive/copy-to-project?workflowInstanceId=${workflowInstanceId}&fileId=${fileId}${
      description ? `&description=${encodeURIComponent(description)}` : ""
    }${targetFolderId ? `&targetFolderId=${encodeURIComponent(targetFolderId)}` : ""}`;
    const knownFiles = await this.listFilesForUploadVerification(
      workflowInstanceId,
      targetFolderId
    );
    const knownFileIds = new Set(
      knownFiles
        .map((existingFile) => existingFile.id)
        .filter(
          (existingFileId): existingFileId is string =>
            typeof existingFileId === "string" && existingFileId.length > 0
        )
    );
    let sourceFileName = "";
    try {
      sourceFileName = (await this.getGoogleDriveFileInfo(fileId))?.name || "";
    } catch (fileInfoError) {
    }

    try {
      const response: AxiosResponse<GoogleDriveAttachment> = await this.api.post(
        url
      );
      return response.data;
    } catch (copyError) {
      if (sourceFileName) {
        const recoveredCopy = await this.detectRecoveredProjectUpload(
          workflowInstanceId,
          sourceFileName,
          knownFileIds,
          targetFolderId
        );

        if (recoveredCopy) {
          return recoveredCopy;
        }
      }

      throw copyError;
    }
  }

  async createProjectFolder(
    workflowInstanceId: number,
    name: string,
    parentFolderId?: string | null
  ): Promise<GoogleDriveAttachment> {
    const response: AxiosResponse<GoogleDriveAttachment> = await this.api.post(
      "/google-drive/project-folders",
      {
        workflowInstanceId,
        name,
        parentFolderId: parentFolderId || null,
      }
    );
    return response.data;
  }

  /**
   * Upload a file to the Activity Templates folder
   * @param file The file to upload
   * @returns Uploaded file information
   */
  async uploadTemplateFile(file: File): Promise<GoogleDriveAttachment> {
    const formData = new FormData();
    formData.append("file", file);

    const response: AxiosResponse<GoogleDriveAttachment> = await this.api.post(
      `/google-drive/upload-template`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  }

  // ✅ NEW: Runtime activity methods (works with any workflow)

  async createAdHocWorkflow(
    projectId: number,
    name: string,
    description: string
  ): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/workflows/ad-hoc`,
      { projectId, name, description }
    );
    return response.data.data;
  }

  async addActivityToWorkflow(
    workflowInstanceId: number,
    activityId: number,
    assigneeId: number,
    formData?: any,
    estimatedDuration?: number,
    submitDescription?: string,
    timeData?: {
      startTime?: string;
      endTime?: string;
      breaks?: Array<{ start: string; end: string }>;
    },
    attachments?: GoogleDriveAttachment[]
  ): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/workflows/${workflowInstanceId}/add-activity`,
      {
        activityId,
        assigneeId,
        formData,
        estimatedDuration,
        submitDescription,
        startTime: timeData?.startTime,
        endTime: timeData?.endTime,
        breaks: timeData?.breaks,
        attachments,
      }
    );
    return response.data;
  }

  async validateWorkTimeRange(
    assigneeId: number,
    startTime?: string,
    endTime?: string,
    ignoreActivityInstanceId?: number
  ): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/workflows/validate-work-time`,
      {
        assigneeId,
        startTime,
        endTime,
        ignoreActivityInstanceId,
      }
    );
    return response.data;
  }

  async assignActivityToWorkflow(
    workflowInstanceId: number,
    activityId: number,
    assigneeIds: number[],
    description?: string
  ): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/workflows/${workflowInstanceId}/assign-activity`,
      {
        activityId,
        assigneeIds,
        description,
      }
    );
    return response.data;
  }

  async getUserWorkflowInstances(userId: number): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(
      `/workflows/instances/user/${userId}`
    );
    return response.data.data;
  }

  async getActivitiesByCompany(
    companyId: number
  ): Promise<ActivityDefinition[]> {
    const response: AxiosResponse<ApiResponse<ActivityDefinition[]>> =
      await this.api.get(`/activities/by-company/${companyId}`);
    return response.data.data;
  }

  async getUserActivityHistory(userId: number): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(
      `/workflows/activity-history/${userId}`
    );
    return response.data.data;
  }

  // Get all activity instances for a project
  async getProjectActivities(workflowContainerId: number): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(
      `/workflows/project-activities/${workflowContainerId}`
    );
    return response.data.data;
  }

  async getMonthlyReportSummary(
    month: number,
    year: number,
    activityTemplateIds?: number[],
    userIds?: number[],
    groupBy?: "project" | "activity" | "user"
  ): Promise<any[]> {
    const params = new URLSearchParams();
    params.append("month", String(month));
    params.append("year", String(year));
    if (activityTemplateIds && activityTemplateIds.length > 0) {
      params.append("activityTemplateIds", activityTemplateIds.join(","));
    }
    if (userIds && userIds.length > 0) {
      params.append("userIds", userIds.join(","));
    }
    if (groupBy) {
      params.append("groupBy", groupBy);
    }
    const response: AxiosResponse<any[]> = await this.api.get(
      `/activity-reports/monthly-summary?${params.toString()}`
    );
    return response.data;
  }
}

export const apiService = new ApiService();
